import type { Disposable } from "@/shared/contracts/lifecycle";
import { HoverChargeExecutionController } from "@/features/hoverIdle/core/hoverChargeExecutionController";
import { getMessages } from "@/i18n/runtime";
import type { HoverIdleSnapshot, NHovBranchId } from "../types";
import {
  createNHovViewModel,
  type NHovBranchCellViewModel,
  type NHovBranchHeadingLabels,
  type NHovViewModel,
} from "./nHovViewModel";
import {
  DEFAULT_NHOV_RESET_ANIMATION_MODE,
  NHovExecutionController,
  resolveNHovAnimationTiming,
  type NHovExecutionState,
  type ResetAnimationMode,
} from "./nHovExecutionAnimation";

export const DEFAULT_NHOV_HOVER_CONFIRM_MS = 5000;

export type NHovHoldDurationMs = 5000 | 2000 | 0;

export interface NHovViewOptions {
  readonly container: HTMLElement;
  readonly onExecute: (branchId: NHovBranchId) => boolean | void;
  readonly getHoldDurationMs?: () => NHovHoldDurationMs;
  readonly getAnimationMode?: () => ResetAnimationMode;
  readonly onLockHovInput?: (locked: boolean) => void;
  readonly onExecutionStateChange?: (state: NHovExecutionState) => void;
}

export class NHovView implements Disposable {
  readonly root: HTMLDivElement;

  private readonly headerEl: HTMLDivElement;
  private readonly cellGrid: HTMLDivElement;
  private readonly onExecute: (branchId: NHovBranchId) => boolean | void;
  private readonly getHoldDurationMs: () => NHovHoldDurationMs;

  private cells: NHovBranchCell[] = [];
  private currentMode: NHovViewModel["mode"] = "first";
  private currentVisibility = false;
  private readonly executionController = new NHovExecutionController();
  private executionTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly getAnimationMode: () => ResetAnimationMode;
  private readonly onLockHovInput: (locked: boolean) => void;
  private readonly onExecutionStateChange: (state: NHovExecutionState) => void;

  constructor(options: NHovViewOptions) {
    const messages = getMessages().hovView.nHov;
    this.onExecute = options.onExecute;
    this.getHoldDurationMs =
      options.getHoldDurationMs ?? (() => DEFAULT_NHOV_HOVER_CONFIRM_MS);
    this.getAnimationMode =
      options.getAnimationMode ?? (() => DEFAULT_NHOV_RESET_ANIMATION_MODE);
    this.onLockHovInput = options.onLockHovInput ?? ((): void => undefined);
    this.onExecutionStateChange =
      options.onExecutionStateChange ?? ((): void => undefined);

    this.root = document.createElement("div");
    this.root.className = "nhov-panel";
    this.root.hidden = true;

    this.headerEl = document.createElement("div");
    this.headerEl.className = "nhov-header";
    this.headerEl.textContent = messages.header;

    this.cellGrid = document.createElement("div");
    this.cellGrid.className = "nhov-cell-grid";

    this.root.append(this.headerEl, this.cellGrid);
    options.container.append(this.root);
  }

  update(snapshot: HoverIdleSnapshot): void {
    const messages = getMessages().hovView.nHov;
    const stock = snapshot.nHov.executionStock;
    this.headerEl.textContent =
      stock == null
        ? messages.header
        : `${messages.header} (${stock.remainingCount}/${stock.effectiveLimit})`;
    const headingLabels: NHovBranchHeadingLabels = {
      hgAmplify: messages.branchHeading.hgAmplify,
      hgDiscount: messages.branchHeading.hgDiscount,
      hngScale: messages.branchHeading.hngScale,
      hgNHovEffectsToHng: messages.branchHeading.hgNHovEffectsToHng,
    };
    const vm = createNHovViewModel({ snapshot, headingLabels });

    this.root.hidden = !vm.isVisible;
    if (this.currentVisibility !== vm.isVisible) {
      this.currentVisibility = vm.isVisible;
      if (!vm.isVisible) {
        this.cancelAllCellHolds();
      }
    }
    if (!vm.isVisible) {
      return;
    }

    this.root.dataset.mode = vm.mode;
    if (this.currentMode !== vm.mode) {
      this.currentMode = vm.mode;
      this.rebuildCells(vm.cells);
    } else if (this.cells.length !== vm.cells.length) {
      this.rebuildCells(vm.cells);
    } else {
      vm.cells.forEach((cellVm, idx) => {
        this.cells[idx]?.update(cellVm);
      });
    }
  }

  private rebuildCells(
    cellViewModels: readonly NHovBranchCellViewModel[],
  ): void {
    for (const cell of this.cells) {
      cell.dispose();
    }
    this.cells = [];
    this.cellGrid.innerHTML = "";

    const cellKind = this.currentMode === "first" ? "execute" : "branch";
    for (const cellVm of cellViewModels) {
      const cell = new NHovBranchCell({
        viewModel: cellVm,
        kind: cellKind,
        onConfirm: (branchId) => this.handleHoldConfirm(branchId),
        getHoldDurationMs: this.getHoldDurationMs,
      });
      this.cells.push(cell);
      this.cellGrid.append(cell.root);
    }
    this.applyExecutingState();
  }

  // The controller guards against reentrancy so a second hold completion in
  // the swap-out → reveal window cannot fire `executeNHov` twice.
  private handleHoldConfirm(branchId: NHovBranchId): void {
    if (!this.executionController.tryStart(branchId)) return;
    this.applyExecutingState();
    this.emitExecutionState();
    this.onLockHovInput(true);

    const timing = resolveNHovAnimationTiming(this.getAnimationMode());
    this.scheduleExecutionTimer(
      () => this.commitExecution(branchId, timing.revealMs),
      timing.swapOutMs,
    );
  }

  private commitExecution(branchId: NHovBranchId, revealMs: number): void {
    let didCommit: boolean | void;
    try {
      didCommit = this.onExecute(branchId);
    } catch (error) {
      this.finishExecution();
      throw error;
    }
    if (didCommit === false) {
      this.finishExecution();
      return;
    }
    if (!this.executionController.setPhase("reveal")) return;
    this.applyExecutingState();
    this.emitExecutionState();
    this.scheduleExecutionTimer(() => this.finishExecution(), revealMs);
  }

  private finishExecution(): void {
    this.clearExecutionTimer();
    this.executionController.complete();
    this.onLockHovInput(false);
    this.applyExecutingState();
    this.emitExecutionState();
  }

  private applyExecutingState(): void {
    const state = this.executionController.getState();
    const executingId = state?.branchId ?? null;
    if (state === null) {
      this.root.removeAttribute("data-executing");
      this.root.removeAttribute("data-execution-phase");
    } else {
      this.root.dataset.executing = "true";
      this.root.dataset.executionPhase = state.phase;
    }
    for (const cell of this.cells) {
      cell.setExecutingState(executingId);
    }
  }

  private emitExecutionState(): void {
    this.onExecutionStateChange(this.executionController.getState());
  }

  private cancelAllCellHolds(): void {
    for (const cell of this.cells) {
      cell.cancelHold();
    }
  }

  private scheduleExecutionTimer(callback: () => void, delayMs: number): void {
    this.clearExecutionTimer();
    if (delayMs <= 0) {
      callback();
      return;
    }
    this.executionTimer = setTimeout(() => {
      this.executionTimer = null;
      callback();
    }, delayMs);
  }

  private clearExecutionTimer(): void {
    if (this.executionTimer === null) return;
    clearTimeout(this.executionTimer);
    this.executionTimer = null;
  }

  dispose(): void {
    this.clearExecutionTimer();
    if (this.executionController.isExecuting()) {
      this.executionController.complete();
      this.onLockHovInput(false);
      this.emitExecutionState();
    }
    this.cancelAllCellHolds();
    for (const cell of this.cells) {
      cell.dispose();
    }
    this.cells = [];
    this.root.remove();
  }
}

type NHovCellKind = "execute" | "branch";

interface NHovBranchCellOptions {
  readonly viewModel: NHovBranchCellViewModel;
  readonly kind: NHovCellKind;
  readonly onConfirm: (branchId: NHovBranchId) => void;
  readonly getHoldDurationMs: () => NHovHoldDurationMs;
}

class NHovBranchCell implements Disposable {
  readonly root: HTMLButtonElement;
  isHovering = false;

  private readonly headingEl: HTMLDivElement;
  private readonly valuesEl: HTMLDivElement;
  private readonly progressBar: HTMLDivElement;
  private readonly xpBarEl: HTMLDivElement;
  private readonly xpBarFillEl: HTMLDivElement;
  private readonly onConfirm: (branchId: NHovBranchId) => void;
  private readonly getHoldDurationMs: () => NHovHoldDurationMs;
  private readonly chargeController: HoverChargeExecutionController;

  private branchId: NHovBranchId;
  private isAvailable: boolean;
  private canExecuteCell = false;
  private isLockedCell = false;

  private readonly handlePointerEnter = (): void => this.beginHold();
  private readonly handlePointerLeave = (): void => this.releaseHold();
  private readonly handlePointerDown = (): void => this.beginHold();
  private readonly handlePointerUp = (): void => this.releaseHold();
  private readonly handlePointerCancel = (): void => this.releaseHold();
  private readonly handleFocus = (): void => this.beginHold();
  private readonly handleBlur = (): void => this.releaseHold();

  constructor(options: NHovBranchCellOptions) {
    this.branchId = options.viewModel.branchId;
    this.isAvailable = options.viewModel.isAvailable;
    this.canExecuteCell = options.viewModel.canExecute;
    this.isLockedCell = options.viewModel.isLocked;
    this.onConfirm = options.onConfirm;
    this.getHoldDurationMs = options.getHoldDurationMs;

    this.root = document.createElement("button");
    this.root.type = "button";
    this.root.className =
      options.kind === "execute"
        ? "nhov-cell nhov-execute-cell"
        : "nhov-cell nhov-branch-cell";
    this.root.dataset.branchId = options.viewModel.branchId;
    this.root.style.setProperty(
      "--nhov-candidate-hue",
      String(options.viewModel.theme.hue),
    );
    this.root.dataset.cellKind = options.kind;

    this.progressBar = document.createElement("div");
    this.progressBar.className = "nhov-cell__fill";
    this.progressBar.setAttribute("aria-hidden", "true");

    this.headingEl = document.createElement("div");
    this.headingEl.className = "nhov-cell__label";

    this.valuesEl = document.createElement("div");
    this.valuesEl.className = "nhov-cell__value";

    this.xpBarEl = document.createElement("div");
    this.xpBarEl.className = "nhov-cell__xp";
    this.xpBarEl.setAttribute("aria-hidden", "true");

    this.xpBarFillEl = document.createElement("div");
    this.xpBarFillEl.className = "nhov-cell__xp-fill";
    this.xpBarEl.append(this.xpBarFillEl);

    this.root.append(
      this.progressBar,
      this.headingEl,
      this.valuesEl,
      this.xpBarEl,
    );
    this.chargeController = new HoverChargeExecutionController({
      getDurationMs: () => this.getHoldDurationMs(),
      canExecute: () => this.canStartHold(),
      onProgress: (progress) => this.setFillProgress(progress),
      onExecute: () => this.fireConfirm(),
    });
    this.applyViewModel(options.viewModel);

    this.root.addEventListener("pointerenter", this.handlePointerEnter);
    this.root.addEventListener("pointerleave", this.handlePointerLeave);
    this.root.addEventListener("pointerdown", this.handlePointerDown);
    this.root.addEventListener("pointerup", this.handlePointerUp);
    this.root.addEventListener("pointercancel", this.handlePointerCancel);
    this.root.addEventListener("focus", this.handleFocus);
    this.root.addEventListener("blur", this.handleBlur);
  }

  setExecutingState(executingBranchId: NHovBranchId | null): void {
    if (executingBranchId === null) {
      this.root.removeAttribute("data-executing");
      this.root.removeAttribute("data-executing-branch");
      this.root.removeAttribute("data-dimmed");
      this.setFillProgress(0);
      return;
    }
    if (executingBranchId === this.branchId) {
      this.cancelHold();
      this.root.dataset.executing = "true";
      this.root.dataset.executingBranch = "true";
      this.root.removeAttribute("data-dimmed");
      this.setFillProgress(1);
    } else {
      this.cancelHold();
      this.root.removeAttribute("data-executing");
      this.root.removeAttribute("data-executing-branch");
      this.root.dataset.dimmed = "true";
    }
  }

  update(vm: NHovBranchCellViewModel): void {
    if (vm.branchId !== this.branchId) {
      this.cancelHold();
      this.branchId = vm.branchId;
      this.root.dataset.branchId = vm.branchId;
    }
    this.root.style.setProperty("--nhov-candidate-hue", String(vm.theme.hue));
    this.applyViewModel(vm);
  }

  private applyViewModel(vm: NHovBranchCellViewModel): void {
    this.isAvailable = vm.isAvailable;
    this.canExecuteCell = vm.canExecute;
    this.isLockedCell = vm.isLocked;
    this.headingEl.textContent = vm.headingLabel;
    this.valuesEl.textContent = vm.freshnessWallActive
      ? `${vm.currentValueLabel} → ${vm.freshNextValueLabel} → ${vm.nextValueLabel}`
      : `${vm.currentValueLabel} → ${vm.nextValueLabel}`;
    this.setXpProgress(vm.branchLevelProgressRatio);
    const enabled = vm.isAvailable && vm.canExecute;
    this.root.disabled = !enabled;
    this.root.dataset.available = vm.isAvailable ? "true" : "false";
    this.root.dataset.canExecute = vm.canExecute ? "true" : "false";
    if (vm.isLocked) {
      this.root.dataset.locked = "true";
      this.root.setAttribute("aria-disabled", "true");
    } else {
      this.root.removeAttribute("data-locked");
      this.root.removeAttribute("aria-disabled");
    }
    if (!enabled) {
      this.cancelHold();
    }
  }

  private beginHold(): void {
    if (this.isHovering) return;
    if (!this.canStartHold()) return;
    this.isHovering = true;
    this.startHold();
  }

  private releaseHold(): void {
    this.isHovering = false;
    this.chargeController.setHovered(false);
  }

  private startHold(): void {
    this.chargeController.setHovered(true);
  }

  cancelHold(): void {
    this.isHovering = false;
    this.chargeController.cancel();
    this.setFillProgress(0);
  }

  private canStartHold(): boolean {
    if (this.root.disabled) return false;
    if (!this.isAvailable) return false;
    if (!this.canExecuteCell) return false;
    return !this.isLockedCell;
  }

  private setFillProgress(progress: number): void {
    const normalizedProgress = Math.min(1, Math.max(0, progress));
    this.progressBar.style.transform = `scaleY(${normalizedProgress})`;
  }

  // Empty (not full) when the ratio cannot be resolved so the bar cannot
  // claim level-up progress that does not exist.
  private setXpProgress(ratio: number): void {
    const normalizedRatio = Number.isFinite(ratio)
      ? Math.min(1, Math.max(0, ratio))
      : 0;
    this.xpBarFillEl.style.transform = `scaleX(${normalizedRatio})`;
  }

  private fireConfirm(): void {
    const branchId = this.branchId;
    this.isHovering = false;
    this.onConfirm(branchId);
  }

  dispose(): void {
    this.cancelHold();
    this.chargeController.dispose();
    this.root.removeEventListener("pointerenter", this.handlePointerEnter);
    this.root.removeEventListener("pointerleave", this.handlePointerLeave);
    this.root.removeEventListener("pointerdown", this.handlePointerDown);
    this.root.removeEventListener("pointerup", this.handlePointerUp);
    this.root.removeEventListener("pointercancel", this.handlePointerCancel);
    this.root.removeEventListener("focus", this.handleFocus);
    this.root.removeEventListener("blur", this.handleBlur);
    this.root.remove();
  }
}
